/**
 * AdmissionRefSection — the patient detail surface's admission_ref
 * pseudonymisation controls. Renders NOTHING for a `passthrough`
 * field (today's free-text behaviour — the token already renders inline in
 * AttributesSection's attribute list, byte-identical to before this design).
 *
 * For a `minted` field, this section is metadata-only + one audited door:
 *   - `linked: false` → an "unlinked" hint plus a Link-now dialog that
 *     submits the ward-declared `link_fields` (first-link AND legacy-patient
 *     catch-up use the SAME endpoint — design §4's relink door).
 *   - `linked: true && can_disclose` → a Reveal button. The dialog states
 *     access is recorded, collects a config-driven reason code plus an
 *     optional free-text justification, and renders
 *     the returned link fields exactly once. That response is local
 *     component state, discarded on close — it is NEVER written to any
 *     query cache, store, or anywhere a background refetch could resurface
 *     it (property P3, "the link never leaks").
 *   - `linked: true && !can_disclose` → a plain "linked" statement, no
 *     reveal affordance (the caller lacks can_view_phi right now).
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldQuestion, Link2, Link2Off } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { OptionChips } from '@/components/clinical/OptionChips'
import { ErrorWithRetry, StaleDataNotice } from '@/components/clinical'
import { ApiError } from '@/api/client'
import {
  usePatientDetail,
  usePatientsConfig,
  useEffectivePatientAttributeFields,
  useRelinkAdmissionRef,
  useDiscloseAdmissionRef,
} from '@/hooks/usePatients'
import { humaniseAttributeKey } from '@/lib/patientLabel'
import { parsePatientAttributeViolations, type PatientResponse } from '@/api/endpoints/patients'

interface AdmissionRefSectionProps {
  patient: PatientResponse
  /** Patient's CURRENT unit — sources the effective schema's declared
   *  `link_fields` for the link-now dialog. Same value AttributesSection
   *  resolves from the encounter history. */
  currentLocId: string | null
}

/** The current unit's declared link_fields (empty when no minted field) —
 *  resolution via the shared floor-vs-unit precedence hook
 *  (useEffectivePatientAttributeFields, usePatients.ts), the same rule the
 *  registration form uses. */
function useMintedLinkFields(currentLocId: string | null) {
  const fields = useEffectivePatientAttributeFields(currentLocId)
  return fields.find((f) => f.strategy === 'minted')?.link_fields ?? []
}

export function AdmissionRefSection({ patient, currentLocId }: AdmissionRefSectionProps) {
  const { t } = useTranslation()
  const { data: detail, isError: isDetailError, isFetching: isDetailFetching, refetch: refetchDetail } = usePatientDetail(patient.token)
  const link = detail?.admission_ref_link
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [revealDialogOpen, setRevealDialogOpen] = useState(false)
  const linkFieldKeys = useMintedLinkFields(currentLocId)

  // A genuine fetch FAILURE (no detail ever loaded) must render a visible
  // failed-to-load state — never silently return null. On this PHI-adjacent
  // surface, null reads as "no linkage exists for this patient", which is
  // indistinguishable from a real passthrough/no-minted-field patient. Only
  // an actual `no link at all` (detail loaded fine, field genuinely absent)
  // stays a silent no-render, below.
  if (!detail && isDetailError) {
    return (
      <section className="rounded-[18px] border border-neutral-200 bg-white p-4">
        <h3 className="mb-3 font-display text-[17px] font-semibold tracking-tight text-neutral-900">
          {t('patientAdmin.admissionRef.title')}
        </h3>
        <ErrorWithRetry
          error={t('patientAdmin.admissionRef.loadError', 'Could not load linkage status.')}
          onRetry={() => void refetchDetail()}
        />
      </section>
    )
  }

  // Nothing to show for passthrough (no linkage exists) or a backend that
  // predates this design (the field is simply absent) — a REAL absence, not
  // a failure (the fetch above genuinely succeeded or is still in flight).
  if (!link || link.strategy !== 'minted') return null

  return (
    <section className="rounded-[18px] border border-neutral-200 bg-white p-4">
      <h3 className="mb-3 font-display text-[17px] font-semibold tracking-tight text-neutral-900">
        {t('patientAdmin.admissionRef.title')}
      </h3>
      {/* A background refetch failing (or in flight) with cached linkage
          status still showing must stay visible — the same PHI-adjacent
          reasoning as the load-failure branch above. */}
      <StaleDataNotice isFetching={isDetailFetching} isError={isDetailError} className="mb-2" />

      {!link.linked ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-[13px] text-tone-warn">
            <Link2Off className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('patientAdmin.admissionRef.unlinkedHint')}
          </p>
          <Button
            type="button"
            size="pill"
            variant="secondary"
            className="min-h-9 px-3 text-[13px]"
            onClick={() => setLinkDialogOpen(true)}
          >
            {t('patientAdmin.admissionRef.linkNow')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-[13px] text-neutral-500">
            <Link2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('patientAdmin.admissionRef.linkedHint')}
          </p>
          {link.can_disclose && (
            <Button
              type="button"
              size="pill"
              variant="outline"
              className="min-h-9 gap-1.5 px-3 text-[13px]"
              onClick={() => setRevealDialogOpen(true)}
            >
              <ShieldQuestion className="h-4 w-4" aria-hidden="true" />
              {t('patientAdmin.admissionRef.reveal')}
            </Button>
          )}
        </div>
      )}

      <AdmissionRefLinkDialog
        patient={patient}
        linkFieldKeys={linkFieldKeys}
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
      />
      <AdmissionRefDisclosureDialog
        patient={patient}
        open={revealDialogOpen}
        onOpenChange={setRevealDialogOpen}
      />
    </section>
  )
}

interface AdmissionRefLinkDialogProps {
  patient: PatientResponse
  linkFieldKeys: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Link-now / correct-linkage dialog (design §4 POST .../admission-ref/link).
 *  Same endpoint serves a first-time link (legacy patient on a since-flipped
 *  minted ward) and a correction (mistyped case number) — the token never
 *  changes either way. */
function AdmissionRefLinkDialog({ patient, linkFieldKeys, open, onOpenChange }: AdmissionRefLinkDialogProps) {
  const { t } = useTranslation()
  const [values, setValues] = useState<Record<string, string>>({})
  const mutation = useRelinkAdmissionRef()
  // `reset` alone, not the whole `mutation`: the useMutation RETURN VALUE is a
  // new object on every state change, so depending on it would wipe the form
  // the instant `mutate()` fires. `reset` is a stable callback, so the effect
  // still runs only when `open` flips — the intent, now stated to the linter
  // rather than around it.
  const { reset: resetLink } = mutation

  useEffect(() => {
    if (open) {
      setValues({})
      resetLink()
    }
  }, [open, resetLink])

  // PARTIAL content is legal (a nurse may know the case number but not a
  // second declared field — same 03:00 logic as registration); only a fully
  // empty submission is rejected server-side, so the gate requires AT LEAST
  // ONE non-empty field, never all of them (bot-review finding #2).
  const hasAnyLinkValue = linkFieldKeys.some((k) => values[k]?.trim())
  const canSubmit = hasAnyLinkValue && !mutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    const link: Record<string, string> = {}
    for (const key of linkFieldKeys) {
      const trimmed = values[key]?.trim()
      if (trimmed) link[key] = trimmed
    }
    mutation.mutate(
      { token: patient.token, link },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  // 422 violations follow the same field/code/detail shape as attribute-PATCH
  // violations (parsePatientAttributeViolations) — an unknown or deny-listed
  // link-field key. Falls back to a generic localized message rather than
  // the raw `.message` text, which may not be nurse-appropriate/localized.
  const violations =
    mutation.error instanceof ApiError && mutation.error.status === 422
      ? parsePatientAttributeViolations(mutation.error.extensions)
      : []
  const genericLinkError =
    mutation.error instanceof ApiError && mutation.error.status === 422 && violations.length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('patientAdmin.admissionRef.linkTitle')}</DialogTitle>
          <DialogDescription>{t('patientAdmin.admissionRef.linkExplanation')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {linkFieldKeys.map((key) => (
            <div key={key} className="space-y-1.5">
              <label htmlFor={`relink-${key}`} className="block text-[13px] font-medium text-neutral-900">
                {t(`patients.admissionLink.fields.${key}`, humaniseAttributeKey(key))}
              </label>
              <Input
                id={`relink-${key}`}
                name={`relink-${key}`}
                value={values[key] ?? ''}
                onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                autoComplete="off"
                disabled={mutation.isPending}
                className="min-h-11"
              />
            </div>
          ))}
          {violations.map((v) => (
            <p key={v.field} className="text-[13px] text-destructive">{v.detail}</p>
          ))}
          {genericLinkError && (
            <p className="text-[13px] text-destructive">{t('patientAdmin.admissionRef.linkError')}</p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="pill"
              className="min-h-11"
              disabled={mutation.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('patientAdmin.actions.cancel')}
            </Button>
            <Button type="submit" size="pill" className="min-h-11" disabled={!canSubmit}>
              {mutation.isPending ? t('patientAdmin.actions.submitting') : t('patientAdmin.admissionRef.linkSubmit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface AdmissionRefDisclosureDialogProps {
  patient: PatientResponse
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Reveal dialog (design §4 POST .../admission-ref/disclosures — "THE one
 *  door"). `justification` is optional free text the caller may add to the
 *  audit record. Renders whatever
 *  keys the server returns in `result.link` (not a client-predicted field
 *  list) — the server, not the schema cache, is authoritative for what a
 *  disclosure actually contains. */
function AdmissionRefDisclosureDialog({ patient, open, onOpenChange }: AdmissionRefDisclosureDialogProps) {
  const { t } = useTranslation()
  const patientsConfig = usePatientsConfig()
  // Deliberately NOT coalesced to `[]` — an absent vocabulary (older backend)
  // and a shipped-but-empty one both mean "there is nothing to pick", and
  // that must be a LOUD, explicit error state (reasonsUnavailable below),
  // never a silently-empty-but-seemingly-functional <select> that lets the
  // nurse submit and 422 with no visible cause.
  const reasonCodes = patientsConfig?.admission_ref?.disclosure_reason_codes
  const reasonsUnavailable = !reasonCodes || reasonCodes.length === 0
  const [reasonCode, setReasonCode] = useState('')
  const [justification, setJustification] = useState('')
  const mutation = useDiscloseAdmissionRef()
  // See AdmissionRefLinkDialog: the stable `reset`, never the whole mutation.
  const { reset: resetDisclose } = mutation

  useEffect(() => {
    if (open) {
      setReasonCode('')
      setJustification('')
      resetDisclose()
    }
  }, [open, resetDisclose])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (reasonsUnavailable || !reasonCode || mutation.isPending) return
    mutation.mutate(
      {
        token: patient.token,
        reason_code: reasonCode,
        ...(justification.trim() ? { justification: justification.trim() } : {}),
      },
    )
  }

  // The disclosed plaintext must not outlive the dialog (the header's P3
  // claim — bot-review round-2 finding #5: this component stays mounted with
  // the detail panel, so mutation.data would otherwise keep the link content
  // resident in React state, DevTools-inspectable on a shared workstation,
  // until the NEXT open). Reset on every close transition, whatever closed
  // it (Done, cancel, backdrop); the on-open reset stays as the belt.
  const handleOpenChange = (next: boolean) => {
    if (!next) mutation.reset()
    onOpenChange(next)
  }

  const invalidReasonCode = mutation.error instanceof ApiError && mutation.error.code === 'INVALID_REASON_CODE'
  const notFound = mutation.error instanceof ApiError && mutation.error.status === 404
  const genericError =
    mutation.isError &&
    !(mutation.error instanceof ApiError && (mutation.error.status === 422 || mutation.error.status === 404))
  const result = mutation.isSuccess ? mutation.data : null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('patientAdmin.admissionRef.resultTitle')}</DialogTitle>
              <DialogDescription>{t('patientAdmin.admissionRef.resultWarning')}</DialogDescription>
            </DialogHeader>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[15px]">
              {Object.entries(result.link).map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="text-neutral-500">
                    {t(`patients.admissionLink.fields.${key}`, humaniseAttributeKey(key))}
                  </dt>
                  <dd className="font-medium text-neutral-900">{value}</dd>
                </div>
              ))}
            </dl>
            <DialogFooter>
              <Button type="button" size="pill" className="min-h-11" onClick={() => handleOpenChange(false)}>
                {t('patientAdmin.admissionRef.done')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{t('patientAdmin.admissionRef.revealTitle')}</DialogTitle>
              <DialogDescription>{t('patientAdmin.admissionRef.revealDescription')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5">
              {/* Chips carry the group's accessible name, so this is a <span>
                  rather than a <label htmlFor> that would point at nothing. */}
              <span className="block text-[13px] font-semibold text-neutral-500">
                {t('patientAdmin.admissionRef.reasonLabel')}
              </span>
              <OptionChips
                id="disclosure-reason"
                legend={t('patientAdmin.admissionRef.reasonLabel')}
                placeholder={t('patientAdmin.admissionRef.reasonPlaceholder')}
                options={(reasonCodes ?? []).map((code) => ({
                  value: code,
                  label: t(`patientAdmin.admissionRef.reasons.${code}`, humaniseAttributeKey(code)),
                }))}
                value={reasonCode}
                onChange={setReasonCode}
                disabled={mutation.isPending}
                // An absent vocabulary is a LOUD error, not "nothing to pick" —
                // the destructive message below stays the real signal.
                emptyLabel={t('patientAdmin.admissionRef.reasonsUnavailable')}
              />
              {reasonsUnavailable && (
                <p className="text-[13px] text-destructive">{t('patientAdmin.admissionRef.reasonsUnavailable')}</p>
              )}
              {invalidReasonCode && (
                <p className="text-[13px] text-destructive">{t('patientAdmin.admissionRef.invalidReasonCode')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="disclosure-justification" className="block text-[13px] font-semibold text-neutral-500">
                {t('patientAdmin.admissionRef.justificationLabel')}
              </label>
              <Textarea
                id="disclosure-justification"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder={t('patientAdmin.admissionRef.justificationPlaceholder')}
                disabled={mutation.isPending}
                rows={3}
              />
            </div>

            {notFound && <p className="text-[13px] text-destructive">{t('patientAdmin.admissionRef.notFound')}</p>}
            {genericError && <p className="text-[13px] text-destructive">{t('patientAdmin.admissionRef.revealFailed')}</p>}

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                size="pill"
                className="min-h-11"
                disabled={mutation.isPending}
                onClick={() => handleOpenChange(false)}
              >
                {t('patientAdmin.actions.cancel')}
              </Button>
              <Button
                type="submit"
                size="pill"
                className="min-h-11"
                disabled={
                  reasonsUnavailable ||
                  !reasonCode ||
                  mutation.isPending
                }
              >
                {mutation.isPending ? t('patientAdmin.admissionRef.revealing') : t('patientAdmin.admissionRef.confirm')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
